<template>
<div>

  <div class="box">
    <ValidationObserver ref="observer" v-slot="{ invalid }">
      <section v-if="tab === 'general'">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2">
            <p class="text-2xl font-bold leading-tight">Select theme</p>
            <p class="text-sm">Your theme setting will apply to this browser only.</p>
            <div class="mt-4">
              <button
                type="button"
                class="btn px-4"
                :class="config.theme === 'light' ? 'btn-purple' : 'border border-gray-700 light:border-gray-300'"
                @click="setTheme('light')"
              >
                Light
              </button>
              <button
                type="button"
                class="btn px-4 ml-2"
                :class="config.theme === 'dark' ? 'btn-purple' : 'border border-gray-700 light:border-gray-300'"
                @click="setTheme('dark')"
              >
                Dark
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2 mt-4">
          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="tax">
              Default tax percentage <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              rules="required"
              name="default tax percentage"
              v-slot="{ errors }"
            >
              <div class="flex items-center">
                <input
                  name="tax"
                  type="text"
                  id="tax"
                  class="form-control form-no-outline h-10 rounded-r-none"
                  v-model="values.defaultTaxPercentage"
                >
                <div class="h-10 px-2 py-2 rounded-r border border-l-0 border-theme-200 light:border-theme-600 focus:outline-none">
                  %
                </div>
              </div>
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="speed-unit">
              Default speed unit <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              name="default speed unit"
              v-slot="{ errors }"
            >
              <div class="relative">
                <select
                  class="form-control pr-8"
                  id="speed-unit"
                  v-model="values.defaultSpeedUnit"
                >
                  <option :value="su.unit" v-for="(su, i) in config.speedUnits" :key="'speed-units-' + i">{{ su.unit }}</option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center text-xs px-2">
                  <i class="fas fa-chevron-down"></i>
                </div>
              </div>
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>

          </div>
        </div>
      </section>

      <section v-if="tab === 'company'">
        <p class="text-2xl font-bold leading-tight">Your company details</p>
        <p class="text-sm">Your company details will show on printed documents.</p>

        <div class="flex flex-wrap -mx-2 mt-4">
          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-name">
              Company name <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              rules="required"
              name="company name"
              v-slot="{ errors }"
            >
              <input
                name="company-name"
                type="text"
                id="company-name"
                class="form-control"
                v-model="values.companyName"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-address">
              Company address <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              rules="required"
              name="company address"
              v-slot="{ errors }"
            >
              <input
                name="company-address"
                type="text"
                id="company-address"
                class="form-control"
                v-model="values.companyAddress"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-post-code">
              Company post code <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              rules="required"
              name="company post code"
              v-slot="{ errors }"
            >
              <input
                name="company-post-code"
                type="text"
                id="company-post-code"
                class="form-control"
                v-model="values.companyPostCode"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-city">
              Company city <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              rules="required"
              name="company city"
              v-slot="{ errors }"
            >
              <input
                name="company-city"
                type="text"
                id="company-city"
                class="form-control"
                v-model="values.companyCity"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-state">
              Company state
            </label>
            <ValidationProvider
              name="company state"
              v-slot="{ errors }"
            >
              <input
                name="company-state"
                type="text"
                id="company-state"
                class="form-control"
                v-model="values.companyState"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-country">
              Company country <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              rules="required"
              name="company country"
              v-slot="{ errors }"
            >
              <div class="relative">
                <select
                  class="form-control pr-8"
                  id="company-country"
                  v-model="values.companyCountry"
                >
                  <option :value="c.name" v-for="(c, i) in Countries" :key="'company-country-' + i">{{ c.name }}</option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center text-xs px-2">
                  <i class="fas fa-chevron-down"></i>
                </div>
              </div>
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-phone">
              Company phone <span class="text-red-500">*</span>
            </label>
            <ValidationProvider
              rules="required"
              name="company phone"
              v-slot="{ errors }"
            >
              <input
                name="company-phone"
                type="text"
                id="company-phone"
                class="form-control"
                v-model="values.companyPhone"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-vat-number">
              Company VAT number
            </label>
            <ValidationProvider
              rules=""
              name="company vat-number"
              v-slot="{ errors }"
            >
              <input
                name="company-vat-number"
                type="text"
                id="company-vat-number"
                class="form-control"
                v-model="values.companyVATNumber"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-vat-number">
              Company IBAN
            </label>
            <ValidationProvider
              rules=""
              name="company IBAN"
              v-slot="{ errors }"
            >
              <input
                name="company-iban"
                type="text"
                id="company-iban"
                class="form-control"
                v-model="values.companyIBAN"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="w-full md:w-1/3 px-2 mb-4">
            <label class="block text-xs font-bold mb-2" for="company-vat-number">
              Company account number
            </label>
            <ValidationProvider
              rules=""
              name="company account number"
              v-slot="{ errors }"
            >
              <input
                name="company-account-number"
                type="text"
                id="company-account-number"
                class="form-control"
                v-model="values.companyAccountNumber"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

      </section>

      <section v-if="tab === 'reminders'">
        <reminder-settings v-model="values" :has-changes="hasChanges"/>
      </section>

      <section v-if="tab === 'images'">

        <div class="flex flex-wrap -mx-4">
          <div
            class="w-full lg:w-1/3 md:w-1/2 px-4 mb-8"
            v-for="(file, name) in files" :key="'image-files-' + name"
          >
            <div class="flex flex-wrap justify-between mb-1 border-b border-theme-200 light:border-theme-600">
              <p class="font-bold text-lg">
                <span>{{ file.title | sentenceCase }}</span>
                <span
                  v-if="name === 'favicon'"
                  class="text-xs font-normal ml-1"
                  v-tooltip="{ content: `
                    <p class='text-sm font-bold border-b border-theme-200 light:border-theme-600 pb-1 mb-1'>Please try the following:</p>
                    <p>• Refresh the page</p>
                    <p>• Clear your browser cache</p>
                    <p>• Close and re-open browser</p>
                  ` }"
                >(not updating?)</span>
              </p>
              <a class="text-red-500 text-sm cursor-pointer" @click="resetFile(name)">reset</a>
            </div>
            <img class="h-10 mt-4" :src="url.resolve(config.URL.API.host, config[file.config])" alt="" height="40">

            <div class="flex flex-col items-start">
              <label class="bg-blue py-2 px-4 rounded-lg shadow-md hover:bg-blue-600 cursor-pointer mt-4">
                <span>Select a file</span>
                <input
                  class="hidden"
                  type="file"
                  :accept="files.accept"
                  name="fileToUpload"
                  id="fileToUpload"
                  @change="upload($event, name)"
                >
              </label>
            </div>

          </div>
        </div>
      </section>
    </ValidationObserver>
  </div>

  <div class="box" v-if="hasChanges">
    <div class="box p-0 pt-4 flex flex-wrap justify-center items-center">
      <button
        type="button"
        class="btn btn-reset"
        @click="reset"
      >
        <i class="fa-xs fas fa-undo"></i>
        <span class="ml-1">RESET</span>
      </button>
      <button
        type="button"
        class="btn btn-save"
        @click="save"
      >
        <i class="fa-xs fas fa-check"></i>
        <span class="ml-2">SAVE</span>
      </button>
    </div>
  </div>

</div>
</template>

<script>
import axios from 'axios'
import url from 'url'
import { mapGetters, mapActions } from 'vuex'
import mimeMatch from 'mime-match'
import ReminderSettings from '@/components/pages/administration/settings/Reminders'

export default {
  components: {
    ReminderSettings,
  },
  data() {
    return {
      url,
      tab: '',
      currencyList: [],
      values: {
        defaultSpeedUnit: '',
        defaultTaxPercentage: 0,
        defaultReminderMethod: '',
        mailDriver: '',
        mailHost: '',
        mailPort: '',
        mailUsername: '',
        mailPassword: '',
        mailEncryption: '',
        mailFromAddress: '',
        mailFromName: '',
        companyName: '',
        companyAddress: '',
        companyPostCode: '',
        companyCity: '',
        companyState: '',
        companyCountry: '',
        companyPhone: '',
        companyVATNumber: '',
        companyIBAN: '',
        companyAccountNumber: '',
        nexmoKey: '',
        nexmoSecret: '',
        nexmoFromNumber: '',
        routeeID: '',
        routeeSecret: '',
        routeeFrom: '',
        smsProvider: '',
      },
      files: {
        logo: {
          title: 'Logo',
          name: 'logo',
          config: 'logoPath',
          data: null,
          accept: 'image/*',
        },
        logoSmall: {
          title: 'Logo small',
          name: 'logo-sm',
          config: 'logoSmallPath',
          data: null,
          accept: 'image/*',
        },
        logoPrint: {
          title: 'Logo for print',
          name: 'logo-pdf',
          config: 'logoPrintPath',
          data: null,
          accept: 'image/jpeg, image/png, image/gif',
        },
        favicon: {
          title: 'Favicon',
          name: 'favicon',
          config: 'faviconPath',
          data: null,
          accept: 'image/png, image/vnd.microsoft.icon',
        },
      },
    }
  },
  computed: {
    hasChanges() {
      let valueNames = Object.keys(this.values)
      for (let i in valueNames) {
        if (this.values[valueNames[i]] !== this.config[valueNames[i]]) {
          return true
        }
      }
      return false
    },
    ...mapGetters({
      config: 'config/config',
      Countries: 'countries/countries',
    })
  },
  mounted() {
    this.reset()
  },
  methods: {
    setTheme(theme) {
      if (this.config.theme === theme) {
        return
      }
      this['config/changeTheme'](theme)
    },
    resetFile(index) {
      const file = this.files[index]
      this.$swal.fire({
        title: 'Reset ' + file.title,
        text: 'Are you sure?',
        type: 'question',
        confirmButtonText: 'Yes',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
      })
        .then((result) => {
          if (result.value) {
            axios.post('settings/reset-image', {
              identifier: file.config
            })
              .then(() => {
                this.$EventBus.$emit('refreshConfig')
              })
          }
        })
    },
    upload(event, fileIndex) {
      if (!this.files[fileIndex]) {
        return
      }
      const files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      const targetFile = this.files[fileIndex]
      let file = files[0]

      let accepts = targetFile.accept.split(', ')
      if (!accepts.filter(mimeMatch(file.type)).length) {
        this.$notify({
          type: 'error',
          title: 'Unsupported file type',
          text: 'Please use: ' + file.accept
        })
        file = null
        return
      }

      let formData = new FormData()
      formData.append('image', file)
      formData.append('image_name', targetFile.name)
      formData.append('setting', targetFile.config)
      axios.post('settings/upload-image',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(() => {
          this.$EventBus.$emit('refreshConfig')
          file = null
        })
        .catch(() => {
          file = null
        })
    },
    reset() {
      this.tab = this.$route.name.split('.')[2]

      let valueNames = Object.keys(this.values)
      for (let i in valueNames) {
        this.values[valueNames[i]] = this.config[valueNames[i]]
      }
    },
    async save () {
      const isValid = await this.$refs.observer.validate()
      if (!isValid) {
        this.$notify({type: 'error', title: 'Form has errors'})
        return
      }
      this._save()
    },
    _save() {
      (async () => {
        let update = []
        let valueNames = Object.keys(this.values)
        for (let i in valueNames) {
          if (this.values[valueNames[i]] !== this.config[valueNames[i]]) {
            update.push(await axios.put('settings/' + valueNames[i], {value: this.values[valueNames[i]]}))
          }
        }

        Promise.all(update)
          .then(() => {
            // the followind is just a group of all actions needed to refresh config
            // for keeping stuff in one place
            this.$EventBus.$emit('refreshConfig')
          })
      })()
    },
    ...mapActions([
      'config/changeTheme'
    ])
  }
}
</script>
